/**
 * Per-project frontend config for the subscription flow (3-step wizard).
 *
 * What changed vs legacy:
 *  - Step list reduced from 5 → 3 (Reconfirm dropped — order summary lives
 *    in the sidebar + payment step now)
 *  - Demo-mode skip-payment logic removed; the new flow charges every signup
 *  - Step IDs renamed: "company" + "account" + "payment"
 *
 * What this file does:
 *  - decides which steps a project shows (LeaveHub skips Organization;
 *    Rentaru and SmartLaw include it as part of the Company step's extra fields)
 *  - holds the post-signup redirect URL per project
 *
 * What this file does NOT do:
 *  - field-level config — that lives in `companyFields.js`
 *  - backend DB routing — that lives in `connectDB.js` on the server
 */

export const PROJECTS = {
  SMART_LAW: 12,
  LEAVE_HUB: 15,
  RENTARU: 16,
};

// Canonical step IDs. Step components key off these strings.
export const STEP = {
  COMPANY: "company",
  ACCOUNT: "account",
  PAYMENT: "payment",
};

const ALL_STEPS = [STEP.COMPANY, STEP.ACCOUNT, STEP.PAYMENT];

// Steps are identical across projects in the new wizard.
// Per-project field differences are handled in companyFields.js, not by
// adding/removing whole steps.
const STEPS_BY_PROJECT = {
  [PROJECTS.SMART_LAW]: ALL_STEPS,
  [PROJECTS.LEAVE_HUB]: ALL_STEPS,
  [PROJECTS.RENTARU]: ALL_STEPS,
};

// Localized labels — keyed by step ID. Component picks lang at render time.
export const STEP_LABELS = {
  th: {
    [STEP.COMPANY]: "บริษัท",
    [STEP.ACCOUNT]: "ผู้ใช้ + แพ็กเกจ",
    [STEP.PAYMENT]: "ชำระเงิน",
  },
  en: {
    [STEP.COMPANY]: "Company",
    [STEP.ACCOUNT]: "Account & Plan",
    [STEP.PAYMENT]: "Payment",
  },
};

const REDIRECT_BY_PROJECT = {
  [PROJECTS.SMART_LAW]: "https://smartlawth.com/#pricing",
  [PROJECTS.LEAVE_HUB]: "https://cms-leavehub-2024.web.app/",
  [PROJECTS.RENTARU]: "https://rentaru-cms-2023.web.app/",
};

const DEFAULT_REDIRECT = "https://pkindev.com/#project";

// ─── Public API ──────────────────────────────────────────────

export const getStepsForProject = (projectId) => {
  return STEPS_BY_PROJECT[Number(projectId)] ?? ALL_STEPS;
};

export const getStepLabel = (stepId, lang = "th") => {
  return STEP_LABELS[lang]?.[stepId] ?? stepId;
};

export const getExpiredRedirectUrl = (projectId) => {
  return REDIRECT_BY_PROJECT[Number(projectId)] ?? DEFAULT_REDIRECT;
};